// Demo data seeder for Habit Tracker
// Each habit demonstrates a different reward/milestone type:
//
//  Wasser trinken    → Habit-Formed-Meilenstein (90+ Tage, 75%+ Rate) → großes Panel mit Option A/B
//  Meditation        → User-Belohnung: Streak-Reward (threshold 7)
//  Liegestütze       → User-Belohnung: total_completions
//  Spaziergang       → User-Belohnung: weekly_rate
//  Journaling        → Auto-Meilenstein: Streak-7 (kleine Celebration)
//  30-Tage-Challenge → Auto-Meilenstein: Streak-30 (mittlere Celebration)
//  Kalt duschen      → Auto-Meilenstein: Streak-60 (große Celebration)

import { saveHabit, saveLog, getAllHabits, deleteHabit } from './habitDB';
import { getLocalDateStr, addDays } from './habitUtils';

function randomBool(p) { return Math.random() < Math.max(0, Math.min(1, p)); }
function randomInt(min, max) { return Math.floor(Math.random() * (max - min + 1)) + min; }

function getMondayStr() {
  const today = new Date();
  const dow = today.getDay();
  const offset = dow === 0 ? -6 : 1 - dow;
  const monday = new Date(today);
  monday.setDate(today.getDate() + offset);
  return getLocalDateStr(monday);
}

// How many due days have passed this week (Mon → yesterday) + 1 for today's click
function calcWeeklyRateThreshold(daysOfWeek) {
  const today = new Date();
  const todayStr = getLocalDateStr(today);
  const mondayStr = getMondayStr();
  let due = 0;
  let cur = new Date(mondayStr + 'T12:00:00');
  while (getLocalDateStr(cur) < todayStr) {
    const dow = cur.getDay();
    if (!daysOfWeek || daysOfWeek.includes(dow)) due++;
    cur.setDate(cur.getDate() + 1);
  }
  const todayDow = today.getDay();
  const todayIsDue = !daysOfWeek || daysOfWeek.includes(todayDow);
  return Math.max(1, todayIsDue ? due + 1 : due);
}

const DEMO_HABITS = [
  // ── 1. Habit-Formed (90d + 75% Rate) ─────────────────────────────────────
  {
    title: 'Wasser trinken',
    type: 'binary',
    frequency: 'daily',
    cue: 'Nach dem Aufstehen',
    completionRate: 0.92,
    startDaysAgo: 95,
    // no reward → only auto habit-formed milestone fires
  },

  // ── 2. User-Belohnung: Streak-7 ───────────────────────────────────────────
  {
    title: 'Meditation',
    type: 'binary',
    frequency: 'daily',
    cue: 'Nach dem Kaffee',
    completionRate: 0.90,
    startDaysAgo: 30,
    rewardType: 'streak',
    rewardThreshold: 7,
    rewardText: 'Massage gönnen',
  },

  // ── 3. User-Belohnung: total_completions ─────────────────────────────────
  {
    title: 'Liegestütze',
    type: 'performance',
    frequency: 'daily',
    cue: 'Morgens um 7',
    completionRate: 0.90,
    startDaysAgo: 30,
    unitLabel: 'Stk',
    minimumTarget: 5,
    currentTarget: 25,
    goalTarget: 25,   // = currentTarget → Goal-Reached bei erstem Check-in
    valueRange: [10, 30],
    rewardType: 'total_completions',
    rewardText: 'Neue Sportschuhe',
  },

  // ── 4. User-Belohnung: weekly_rate ───────────────────────────────────────
  {
    title: 'Spaziergang',
    type: 'binary',
    frequency: 'custom',
    daysOfWeek: [1, 3, 5],
    cue: 'Nach der Arbeit',
    completionRate: 0.90,
    startDaysAgo: 70,
    rewardType: 'weekly_rate',
    rewardText: 'Café-Besuch',
  },

  // ── 5. Auto: Streak-7 (kleine Celebration) ───────────────────────────────
  {
    title: 'Journaling',
    type: 'binary',
    frequency: 'daily',
    cue: 'Abends vor dem Schlafen',
    completionRate: 1.0,
    startDaysAgo: 6,
    forceAllDone: true,
    // streak = 6 → click today = 7 → kleine Celebration
  },

  // ── 6. Auto: Streak-30 (mittlere Celebration) ────────────────────────────
  {
    title: '30-Tage-Challenge',
    type: 'binary',
    frequency: 'daily',
    cue: 'Morgens nach dem Aufwachen',
    completionRate: 1.0,
    startDaysAgo: 29,
    forceAllDone: true,
    // streak = 29 → click today = 30 → mittlere Celebration
  },

  // ── 7. Auto: Streak-60 (große Celebration) ───────────────────────────────
  {
    title: 'Kalt duschen',
    type: 'binary',
    frequency: 'daily',
    cue: 'Morgens',
    completionRate: 1.0,
    startDaysAgo: 59,
    forceAllDone: true,
    // streak = 59 → click today = 60 → große Celebration
  },
];

export async function seedDemoData() {
  const existing = await getAllHabits();
  for (const h of existing) await deleteHabit(h.id);

  const today = getLocalDateStr();
  const mondayStr = getMondayStr();

  for (const demo of DEMO_HABITS) {
    const startDate = addDays(today, -demo.startDaysAgo);
    const habitId = `habit_demo_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
    const logsToSave = [];

    for (let d = 1; d <= demo.startDaysAgo; d++) {
      const date = addDays(today, -d);

      // Skip non-due days for custom frequency
      if (demo.frequency === 'custom' && demo.daysOfWeek) {
        const dow = new Date(date + 'T12:00:00').getDay();
        if (!demo.daysOfWeek.includes(dow)) continue;
      }

      // Force-done logic
      const isCurrentWeekDay = date >= mondayStr && date < today;
      let forceDone = false;

      if (demo.forceAllDone) {
        // All days done → full consecutive streak = startDaysAgo
        forceDone = true;
      } else if (demo.rewardType === 'streak') {
        // Force last 6 days → streak 6 → today = 7 → user reward fires
        forceDone = d >= 1 && d <= 6;
      } else if (demo.rewardType === 'weekly_rate') {
        // Force current-week days done → today click = threshold
        forceDone = isCurrentWeekDay || (d >= 1 && d <= 4);
      } else if (demo.rewardType === 'total_completions') {
        // Force last 3 days done, rest natural
        forceDone = d >= 1 && d <= 3;
      } else {
        // habit-formed: natural completion, high rate ensures ≥75%
        forceDone = d >= 1 && d <= 3;
      }

      // recency bonus — for forceAllDone habits no bonus needed
      const recencyBonus = demo.forceAllDone ? 0
        : demo.rewardType === 'streak' ? (d < 7 ? 0 : -1.0)
        : (d < 7 ? 0.15 : d < 14 ? 0.05 : -0.05);

      const done = forceDone || randomBool(demo.completionRate + recencyBonus);
      if (!done) continue;

      logsToSave.push({
        id: `${habitId}_${date}`,
        habitId,
        date,
        done: true,
        value: demo.type === 'performance' ? randomInt(demo.valueRange[0], demo.valueRange[1]) : null,
        rating: randomBool(0.3) ? 'easy' : randomBool(0.5) ? 'ok' : 'hard',
        note: '',
        createdAt: new Date(date + 'T09:00:00').toISOString(),
      });
    }

    // Build reward rules
    let rewardRules = [];
    if (demo.rewardType === 'streak') {
      rewardRules = [{ ruleType: 'streak', threshold: demo.rewardThreshold || 7, rewardText: demo.rewardText }];
    } else if (demo.rewardType === 'weekly_rate') {
      rewardRules = [{ ruleType: 'weekly_rate', threshold: calcWeeklyRateThreshold(demo.daysOfWeek || null), rewardText: demo.rewardText }];
    } else if (demo.rewardType === 'total_completions') {
      rewardRules = [{ ruleType: 'total_completions', threshold: logsToSave.length + 1, rewardText: demo.rewardText }];
    }

    const habit = {
      id: habitId,
      title: demo.title,
      type: demo.type,
      status: demo.startDaysAgo > 20 ? 'maintenance' : 'new',
      startDate,
      schedule: { frequencyType: demo.frequency, daysOfWeek: demo.daysOfWeek || [], weeklyTargetCount: null },
      ifThen: { cuePreset: null, cueCustom: demo.cue, actionPhrase: null },
      tracking: demo.type === 'performance' ? {
        unitLabel: demo.unitLabel,
        minimumTarget: demo.minimumTarget,
        currentTarget: demo.currentTarget,
        goalTarget: demo.goalTarget,
        progressionMode: 'manual',
      } : null,
      rewards: { bigRewardsEnabled: rewardRules.length > 0, bigRewardRules: rewardRules },
      gamification: { xpPerCompletion: 10 },
      notifications: { reminderEnabled: false, reminderTime: null, recoveryNudgeEnabled: true },
      createdAt: new Date(startDate + 'T08:00:00').toISOString(),
      updatedAt: new Date().toISOString(),
    };

    await saveHabit(habit);
    for (const log of logsToSave) await saveLog(log);
  }

  return true;
}

export async function clearDemoData() {
  const existing = await getAllHabits();
  for (const h of existing) await deleteHabit(h.id);
  return true;
}
